import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, handleApiError } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeftIcon, PlayIcon, PauseIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { Campaign } from '../lib/types';

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api.get<{ campaign: Campaign & { recipient_counts: { total: number; pending: number; sent: number } } }>(`/campaigns/${id}`),
    enabled: !!id,
  });

  const sendMutation = useMutation({
    mutationFn: () => api.post(`/campaigns/${id}/send`),
    onSuccess: () => {
      toast.success('Campaign queued for sending');
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
    onError: handleApiError,
  });

  const pauseMutation = useMutation({
    mutationFn: () => api.post(`/campaigns/${id}/pause`),
    onSuccess: () => {
      toast.success('Campaign paused');
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
    onError: handleApiError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/campaigns/${id}`),
    onSuccess: () => {
      toast.success('Campaign deleted');
      window.location.href = '/campaigns';
    },
    onError: handleApiError,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-dark-700 rounded" />
        <div className="h-64 bg-dark-700 rounded" />
      </div>
    );
  }

  if (!campaign?.campaign) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-medium text-white">Campaign not found</h2>
        <Link to="/campaigns" className="btn-primary mt-4">
          Back to Campaigns
        </Link>
      </div>
    );
  }

  const c = campaign.campaign;
  const openRate = c.sent_count > 0 ? ((c.open_count / c.sent_count) * 100).toFixed(1) : '0';
  const clickRate = c.sent_count > 0 ? ((c.click_count / c.sent_count) * 100).toFixed(1) : '0';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/campaigns" className="btn-ghost p-2">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">{c.name}</h1>
            <p className="text-gray-400">{c.subject}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {c.status === 'draft' || c.status === 'paused' ? (
            <button
              onClick={() => sendMutation.mutate()}
              disabled={sendMutation.isPending || (c.recipient_counts?.pending || 0) === 0}
              className="btn-primary"
            >
              <PlayIcon className="w-5 h-5 mr-2" />
              {sendMutation.isPending ? 'Sending...' : 'Send Campaign'}
            </button>
          ) : c.status === 'sending' ? (
            <button
              onClick={() => pauseMutation.mutate()}
              disabled={pauseMutation.isPending}
              className="btn-secondary"
            >
              <PauseIcon className="w-5 h-5 mr-2" />
              {pauseMutation.isPending ? 'Pausing...' : 'Pause'}
            </button>
          ) : null}
          {c.status !== 'sending' && (
            <button
              onClick={() => {
                if (confirm('Are you sure you want to delete this campaign?')) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="btn-ghost text-red-400 hover:text-red-300"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-gray-400">Status</p>
          <p className="text-2xl font-semibold text-white capitalize">{c.status}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-400">Recipients</p>
          <p className="text-2xl font-semibold text-white">{(c.recipient_counts?.total || 0).toLocaleString()}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-400">Sent</p>
          <p className="text-2xl font-semibold text-white">{c.sent_count.toLocaleString()}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-400">Pending</p>
          <p className="text-2xl font-semibold text-white">{(c.recipient_counts?.pending || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Performance */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Performance</h2>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-400">Opens</p>
            <p className="text-3xl font-bold text-white">{c.open_count.toLocaleString()}</p>
            <p className="text-sm text-primary-400">{openRate}% open rate</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Clicks</p>
            <p className="text-3xl font-bold text-white">{c.click_count.toLocaleString()}</p>
            <p className="text-sm text-primary-400">{clickRate}% click rate</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Created</p>
            <p className="text-lg font-medium text-white">{new Date(c.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Recipients Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Recipients</h2>
          <Link to={`/campaigns/${id}/recipients`} className="text-sm text-primary-400 hover:text-primary-300">
            Manage Recipients
          </Link>
        </div>
        <p className="text-gray-400">
          {c.recipient_counts?.total || 0} total recipients,{' '}
          {(c.recipient_counts?.pending || 0).toLocaleString()} pending
        </p>
      </div>
    </div>
  );
}
